<template>
  <div class="table-container">
    <ScreenOptions v-if="!config?.disabledRenderScreenOptions" :config="configScreenOptions" :class="showScreenOptions ? 'show' : ''" @toggleScreenOptions="showScreenOptions = !showScreenOptions" />
    <TableFilters :config="configTableFilters" position="top" />
    <div class="main-card scrollbar m-0 p-0 col-12 card">
      <div class="card-body p-0">
        <div class="table-responsive" ref="table">
          <table :class="`m-0 table table-list ${config.tableId}`" :data-table="config.tableId">
            <thead>
              <TableHead :config="configTableHead" position="top" />
            </thead>
            <tbody class="content-wrap">
              <TableBody :config="configTableBody" />
            </tbody>
            <tfoot>
              <TableHead :config="configTableHead" position="bottom" />
            </tfoot>
          </table>
        </div>
      </div>
    </div>
    <TableFilters :config="configTableFilters" position="bottom"></TableFilters>
  </div>
</template>

<script>
import Event from '@/helper/event.js'
import ScreenOptions from './TableList/ScreenOptions.vue'
import TableFilters from './TableList/TableFilters.vue'
import TableHead from './TableList/TableHead.vue'
import TableBody from './TableList/TableBody.vue'
import * as plugins from './TableList/Plugins'
import * as cols from './TableList/Cols'

export default {
  mixins: [Event],
  components: {
    ...cols,
    ...plugins,
    TableFilters,
    TableHead,
    TableBody,
    ScreenOptions
  },
  data() {
    return {
      loaded: false,
      newTable: false,
      showScreenOptions: false,
      renderScreenOptions: false
    }
  },
  mounted() {
    this.$emitter.on('TableList.Refresh', this.refreshTable)
  },
  unmounted() {
    this.$emitter.off('TableList.Refresh', this.refreshTable)
  },
  computed: {
    storeId() {
      return this.config?.storeId
    },
    indexUri() {
      return this.config?.indexUri
    },
    preload() {
      return this.config?.preload
    },
    query() {
      return this.store.getter('query', {})
    },
    more() {
      return this.store.getter('more', {})
    },
    cb() {
      return Object.entries(this.store.getter('rows', {})).reduce((acc, [, row]) => {
        acc[row._id] = row.cb || false
        return acc
      }, {})
    },
    cols() {
      let cols = Object.assign(
        {
          cb: {
            hidden: true,
            priority: 1
          }
        },
        this.store.getter('cols') || {}
      )

      return Object.fromEntries(
        Object.entries(cols).sort(([, a], [, b]) => {
          return (a.priority || 10) - (b.priority || 10)
        })
      )
    },
    rows() {
      return this.store.getter('rows') || []
    },
    extraAttributes() {
      return this.store.getter('extra_attributes') || {}
    },
    screenOptions() {
      return this.store.getter('screen_options') || {}
    },
    isSubOrder() {
      return this.config?.isSubOrder || false
    },
    configScreenOptions() {
      return {
        store: this.store,
        storeId: this.storeId,
        isSubOrder: this.isSubOrder,
        screenOptions: this.screenOptions,
        cols: this.cols,
        show: this.showScreenOptions
      }
    },
    configTableFilters() {
      return {
        store: this.store,
        storeId: this.storeId,
        cb: this.cb,
        tableConfig: this.config,
        components: this.$util.filter(this.$options.components, ({ key }) => {
          return key.startsWith('Plugin')
        })
      }
    },
    configTableHead() {
      return {
        store: this.store,
        storeId: this.storeId,
        screenOptions: this.screenOptions,
        cols: this.cols,
        rows: this.rows,
        cb: this.cb,
        components: this.$util.filter(this.$options.components, ({ key }) => {
          return key.startsWith('ColHead')
        })
      }
    },
    configTableBody() {
      return {
        store: this.store,
        storeId: this.storeId,
        screenOptions: this.screenOptions,
        extraAttributes: this.extraAttributes,
        cols: this.cols,
        rows: this.rows,
        cb: this.cb,
        loaded: this.loaded,
        more: this.more,
        components: this.$util.filter(this.$options.components, ({ key }) => {
          return key.startsWith('ColBody')
        })
      }
    }
  },
  methods: {
    initialization() {
      if (_.isObjectLike(this.$route?.query) && !_.isEmpty(this.$route.query)) {
        this.store.commit('query', Object.assign({}, this.query, this.$route.query))
      } else if (!this.config?.isSubTable) {
        const sessionPath = this.$route?.path?.replace(/\//g, '_') || 'default'
        const sessionQuery = this.$util.sessionStorage.get(`table_query_${sessionPath}`)
        if (_.isObjectLike(sessionQuery) && !_.isEmpty(sessionQuery)) {
          this.store.commit('query', Object.assign({}, this.query, sessionQuery))
        }
      }
      this.refreshTable()
    },
    refreshTable(firstPage, action) {
      this.$emitter.emit('close-all-dropdown')
      if (firstPage) {
        this.store.commit('query.page', 1)
      }
      if (_.isFunction(this?.[action])) {
        this[action]()
      } else {
        let query = _.reduce(
          this.query,
          (result, item, key) => {
            let value = _.isPlainObject(item) ? item?.value : item
            if (value) {
              result[key] = value
            }
            return result
          },
          {}
        )

        this.fetchData(query, {
          $el: this.$refs.table
        })
      }
    },
    setUpData(rawData) {
      this.loaded = true
      if (rawData.status != 'success') return
      let cb = this.cb
      this.$emitter.emit('table-list.refreshed', {
        storeId: this.storeId,
        query: this.query,
        cb: cb
      })
      rawData.data.cols = Object.assign({}, this.cols, rawData.data.cols)
      rawData.data.more = this.more
      rawData.data.query = Object.assign(
        {
          total: 0,
          limit: 10,
          page: 1
        },
        this.query || {},
        rawData.data.query
      )

      if (rawData.data.screen_options.table_name) {
        if (this.screenOptions.table_name != rawData.data.screen_options.table_name) {
          this.newTable = false
        }

        if (!this.newTable) {
          this.newTable = true

          let hiddenCols = []
          Object.entries(this.cols).forEach(([id, col]) => {
            if (this.config.hiddenCols?.[id] != undefined) {
              if (this.config.hiddenCols[id] === true) hiddenCols.push(id)
            } else if (col?.hidden == true) {
              hiddenCols.push(id)
            }
          })

          Object.entries(rawData.data.cols).forEach(([id]) => {
            if (hiddenCols.indexOf(id) != -1) {
              rawData.data.cols[id].hidden = true
            } else {
              rawData.data.cols[id].hidden = false
            }
          })
          Object.entries(rawData.data.rows).forEach(([, item]) => {
            item.cb = cb[item._id] || false
          })
        }
      }

      this.store.commit('', rawData.data)
    }
  },
  watch: {
    cb: {
      handler(values) {
        this.$emitter.emit('table-list.cb-changed', {
          storeId: this.storeId,
          cb: values
        })
      },
      deep: true
    },
    query(value) {
      if (!this.config?.isSubTable) {
        const sessionPath = this.$route?.path?.replace(/\//g, '_') || 'default'
        this.$util.sessionStorage.set(`table_query_${sessionPath}`, this.$util.dataDiff(value, this.$route.query, null, ['total', 'page', 'limit']))
      }
    }
  },
  props: {
    config: Object
  }
}
</script>
<style lang="scss">
.table-container {
  > .main-card {
    overflow: auto;
    border-radius: var(--border-radius, 0.25rem);
    .card-body {
      min-width: 1100px;
    }
  }
  .column-cb {
    input {
      position: relative;
      width: 14px;
      height: 14px;
      opacity: 1;
    }
  }
}
</style>
